import {
  FATORES_DE_ALOCACAO,
  type FatorDeAlocacao,
  PESO_MAXIMO_DE_FATOR,
  PESOS_PADRAO,
  ROTULO_DO_FATOR,
  salvarPesosDeAlocacaoSchema,
} from '@locaveri/shared'
import { LoaderCircle, RotateCcw } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { usePesos, useSalvarPesos } from '@/features/agenda/api'
import { errorMessage } from '@/lib/api'
import { usePermissao } from '@/lib/permissoes'

/**
 * Pesos de alocação — quanto cada fator pesa quando o algoritmo escolhe a placa.
 *
 * ---------------------------------------------------------------------------
 * PESO ORDENA; ELEGIBILIDADE ELIMINA
 * ---------------------------------------------------------------------------
 * A distinção que esta tela precisa gritar: blindagem vencendo no período,
 * documento vencido, status impeditivo e agenda ocupada tiram o candidato da
 * disputa ANTES do score — nenhum peso os traz de volta (Gap 3: bloqueio duro,
 * não alerta). Os sete fatores daqui só ordenam quem já pode sair. Sem essa
 * frase na tela, o gestor zeraria "folga da blindagem" achando que liberou
 * carro com certificado vencido.
 *
 * ---------------------------------------------------------------------------
 * É POLÍTICA DO GESTOR, NÃO CONSTANTE DE CÓDIGO
 * ---------------------------------------------------------------------------
 * A permissão é a de `rental_policy` — quem opera aloca, quem gere decide o
 * critério — e `padrao: true` na leitura significa que NINGUÉM decidiu ainda:
 * está valendo `PESOS_PADRAO` de fábrica, e a tela diz isso em voz alta, como
 * a política de locação faz com `definida`.
 *
 * ---------------------------------------------------------------------------
 * O PERCENTUAL É APRESENTAÇÃO
 * ---------------------------------------------------------------------------
 * A barra de proporção divide inteiros para responder "quanto deste critério é
 * rodízio" — não é dinheiro, não é o score, e o algoritmo não a usa: o motor
 * recebe os pesos crus e faz a média ponderada em inteiros do lado dele.
 */

const DESCRICAO_DO_FATOR: Readonly<Record<FatorDeAlocacao, string>> = {
  PRIORIDADE_MANUAL:
    'Respeita a preferência definida no cadastro do veículo — o desempate que a operação já decidiu à mão.',
  RODIZIO_DE_USO:
    'Prefere o carro que rodou menos, equalizando a quilometragem para a frota envelhecer (e se renovar) junta.',
  FOLGA_ATE_A_REVISAO:
    'Prefere quem tem mais quilômetros de sobra até a próxima revisão, para a locação não devolver o carro direto para a oficina.',
  PROXIMIDADE_DA_BASE:
    'Prefere o carro que já está na base da retirada — sem manobrista deslocando veículo para posicionar.',
  CUSTO_POR_KM:
    'Prefere o carro que custa menos por quilômetro rodado, mandando para a rua o que dá mais margem.',
  FOLGA_DA_BLINDAGEM:
    'Prefere o certificado de blindagem com mais folga além da devolução — vencer dentro do período já elimina o candidato; isto só desempata.',
  ENCAIXE_NA_MALHA:
    'Prefere a agenda em que a reserva deixa menos buraco antes e depois — menos horas de carro parado entre locações.',
}

/** O formulário guarda a ENTRADA (strings); o Zod coage na validação. */
type CamposDePesos = Readonly<Record<FatorDeAlocacao, string>>

function paraCampos(pesos: Readonly<Record<FatorDeAlocacao, number>>): CamposDePesos {
  return Object.fromEntries(
    FATORES_DE_ALOCACAO.map((fator) => [fator, String(pesos[fator])]),
  ) as CamposDePesos
}

/** O inteiro digitado, para o slider e o percentual. Campo quebrado vale 0. */
function pesoInteiro(valor: string): number {
  const numero = Number(valor)

  return Number.isInteger(numero) && numero >= 0 ? numero : 0
}

function ehFator(valor: unknown): valor is FatorDeAlocacao {
  return typeof valor === 'string' && (FATORES_DE_ALOCACAO as readonly string[]).includes(valor)
}

export function PesosDeAlocacaoPage(): ReactElement {
  const podeEditar = usePermissao('rental_policy.update')

  const consulta = usePesos()
  const salvar = useSalvarPesos()

  const [campos, setCampos] = useState<CamposDePesos | null>(null)
  const [erros, setErros] = useState<Readonly<Partial<Record<FatorDeAlocacao, string>>>>({})
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  // Preenche o formulário quando a leitura chega — uma vez. Depois disso o
  // estado é da pessoa: um refetch em segundo plano não pode apagar o que ela
  // está ajustando.
  useEffect(() => {
    if (campos === null && consulta.data !== undefined) {
      setCampos(paraCampos(consulta.data.pesos))
    }
  }, [campos, consulta.data])

  const ocupado = salvar.isPending

  function definirPeso(fator: FatorDeAlocacao, valor: string): void {
    setCampos((atuais) => (atuais === null ? atuais : { ...atuais, [fator]: valor }))
  }

  /** Volta aos `PESOS_PADRAO` NA TELA — só o salvar grava. */
  function restaurarPadrao(): void {
    setCampos(paraCampos(PESOS_PADRAO))
    setErros({})
    setErroGeral(null)
  }

  function aoSalvar(): void {
    if (campos === null) {
      return
    }

    setErros({})
    setErroGeral(null)

    const validado = salvarPesosDeAlocacaoSchema.safeParse(campos)

    if (!validado.success) {
      const doCampo: Partial<Record<FatorDeAlocacao, string>> = {}
      const sobras: string[] = []

      for (const issue of validado.error.issues) {
        const campo = issue.path[0]

        if (ehFator(campo)) {
          doCampo[campo] = issue.message
        } else {
          // O refinamento "todos zerados" não tem campo — é a mensagem geral.
          sobras.push(issue.message)
        }
      }

      setErros(doCampo)
      setErroGeral(sobras.length > 0 ? sobras.join(' · ') : null)
      return
    }

    // Aqui vale `validado.data`, ao contrário dos formulários de dinheiro: este
    // schema só COAGE string para inteiro — nada de `bigint` nem `Date`, que
    // são os tipos que `JSON.stringify` não sabe serializar.
    salvar.mutate(validado.data, {
      onSuccess: (resposta) => {
        toast.success('Pesos salvos', {
          description: 'Valem das próximas alocações em diante — as já feitas não mudam.',
        })
        setCampos(paraCampos(resposta.pesos))
      },
      onError: (causa: unknown) => {
        const mensagem = errorMessage(causa)

        setErroGeral(mensagem)
        toast.error('Não foi possível salvar os pesos', { description: mensagem })
      },
    })
  }

  const total =
    campos === null
      ? 0
      : FATORES_DE_ALOCACAO.reduce((soma, fator) => soma + pesoInteiro(campos[fator]), 0)

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Pesos de alocação</h1>
        <p className="max-w-prose text-sm text-fg-secondary">
          Quanto cada fator pesa quando o algoritmo escolhe a placa de uma reserva aprovada. Peso
          decide QUAL carro sai — nunca SE há carro, nem por quanto.
        </p>
      </div>

      {podeEditar ? null : (
        <Alert variant="neutral">
          <AlertDescription>
            Somente leitura: ajustar os pesos exige a permissão de política de locação
            (rental_policy.update).
          </AlertDescription>
        </Alert>
      )}

      <Alert variant="info">
        <AlertTitle>Peso não compra elegibilidade</AlertTitle>
        <AlertDescription>
          Blindagem vencendo no período, documento vencido, status impeditivo e agenda ocupada
          eliminam o candidato ANTES do score — nenhum peso os traz de volta. Os fatores abaixo só
          ordenam quem já pode sair.
        </AlertDescription>
      </Alert>

      {consulta.isPending ? (
        <LoadingState rotulo="Carregando os pesos…" linhas={4} />
      ) : consulta.isError ? (
        <ErrorState
          titulo="Não foi possível carregar os pesos"
          erro={consulta.error}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
        />
      ) : campos === null ? (
        <LoadingState rotulo="Carregando os pesos…" linhas={4} />
      ) : (
        <>
          {consulta.data.padrao ? (
            <Alert variant="warning">
              <AlertTitle>Está valendo o padrão de fábrica</AlertTitle>
              <AlertDescription>
                Ninguém salvou os pesos ainda — estes números são o ponto de partida do código, não
                uma decisão da casa. Salve para fixar a decisão.
              </AlertDescription>
            </Alert>
          ) : null}

          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-3 lg:grid-cols-2">
            {FATORES_DE_ALOCACAO.map((fator) => {
              const valor = campos[fator]
              const peso = pesoInteiro(valor)
              const percentual = total > 0 ? Math.round((peso * 100) / total) : 0
              const erro = erros[fator]

              return (
                <div
                  key={fator}
                  className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Label
                        htmlFor={`peso-${fator}`}
                        className="text-sm font-medium text-foreground"
                      >
                        {ROTULO_DO_FATOR[fator]}
                      </Label>
                      <p className="mt-1 text-xs text-fg-secondary">{DESCRICAO_DO_FATOR[fator]}</p>
                    </div>
                    {peso === 0 ? <Badge variant="neutral">Desligado</Badge> : null}
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={0}
                      max={PESO_MAXIMO_DE_FATOR}
                      step={1}
                      value={peso}
                      aria-label={`Peso de ${ROTULO_DO_FATOR[fator]}`}
                      disabled={!podeEditar || ocupado}
                      onChange={(evento) => {
                        definirPeso(fator, evento.target.value)
                      }}
                      className="h-2 min-w-0 flex-1 cursor-pointer accent-fg-secondary disabled:cursor-not-allowed"
                    />
                    <Input
                      id={`peso-${fator}`}
                      type="number"
                      min={0}
                      max={PESO_MAXIMO_DE_FATOR}
                      inputMode="numeric"
                      value={valor}
                      disabled={!podeEditar || ocupado}
                      onChange={(evento) => {
                        definirPeso(fator, evento.target.value)
                      }}
                      className="tabular-money w-20 shrink-0 text-right"
                    />
                  </div>

                  {erro === undefined ? null : <p className="text-xs text-danger-fg">{erro}</p>}

                  {/* Proporção RELATIVA: apresentação sobre inteiros, nunca o
                      score — o motor recebe os pesos crus. */}
                  <div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-elevated">
                      <div
                        className="h-full rounded-full bg-fg-secondary"
                        style={{ width: `${String(percentual)}%` }}
                      />
                    </div>
                    <p className="mt-1 text-xs tabular-money text-fg-muted">
                      {total === 0
                        ? 'Sem critério — todos os pesos estão zerados.'
                        : `${String(percentual)}% do critério`}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {podeEditar ? (
              <>
                <Button type="button" onClick={aoSalvar} disabled={ocupado}>
                  {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
                  Salvar pesos
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={ocupado}
                  onClick={restaurarPadrao}
                >
                  <RotateCcw aria-hidden="true" />
                  Restaurar padrão
                </Button>
              </>
            ) : null}
            <p className="text-xs text-fg-muted">
              Peso 0 desliga o fator. Todos zerados o servidor recusa — sem critério não há escolha.
            </p>
          </div>
        </>
      )}
    </div>
  )
}
